/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Resolves Box.ownerUID for the access resolver (isOwner is document truth,
 * never a claim). useUserAccess has dozens of consumers, so the read is cached
 * per boxID for the whole session: one Box read per gym per tab, not one per
 * mount. Ownership only changes via the admin transfer script; a reload picks
 * that up.
 */

import { useState, useEffect } from "react";
import { getBox } from "@lemonadetraining/web-shared";

interface UseBoxOwnerUIDResult {
  ownerUID: string | null;
  isLoading: boolean;
}

const boxByID = new Map<
  string,
  Promise<{ ownerUID: string | null; name: string | null; logoURL: string | null }>
>();

const loadBox = (boxID: string) => {
  const cached = boxByID.get(boxID);
  if (cached) return cached;
  const pending = getBox(boxID)
    .then((box) => ({
      ownerUID: box?.ownerUID ?? null,
      name: box?.name ?? null,
      logoURL: box?.logoURL ?? null,
    }))
    .catch((err) => {
      console.error("Error fetching box owner:", err);
      boxByID.delete(boxID);
      return { ownerUID: null, name: null, logoURL: null };
    });
  boxByID.set(boxID, pending);
  return pending;
};

const loadOwnerUID = (boxID: string): Promise<string | null> =>
  loadBox(boxID).then((box) => box.ownerUID);

/** The gym's name from the same cached Box read; null when unreadable. */
export const loadBoxName = (boxID: string): Promise<string | null> =>
  loadBox(boxID).then((box) => box.name);

/** The gym's name and logo for the sidebar, from the same cached Box read. */
export const loadBoxHeader = (
  boxID: string
): Promise<{ name: string | null; logoURL: string | null }> =>
  loadBox(boxID).then((box) => ({ name: box.name, logoURL: box.logoURL }));

export const _resetBoxOwnerCacheForTests = (): void => {
  boxByID.clear();
};

export const useBoxOwnerUID = (boxID: string | null): UseBoxOwnerUIDResult => {
  const [state, setState] = useState<{ boxID: string | null; ownerUID: string | null }>({
    boxID: null,
    ownerUID: null,
  });

  useEffect(() => {
    if (!boxID) return;
    let isMounted = true;
    loadOwnerUID(boxID).then((ownerUID) => {
      if (isMounted) setState({ boxID, ownerUID });
    });
    return () => {
      isMounted = false;
    };
  }, [boxID]);

  const resolved = boxID !== null && state.boxID === boxID;
  return {
    ownerUID: resolved ? state.ownerUID : null,
    isLoading: boxID !== null && !resolved,
  };
};
